import React, {useId, useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, DialogActions,
    DialogContent, DialogTitle, FormControlLabel, TextField, Typography, useMediaQuery, useTheme} from '@mui/material';
import {Check, ContentCopyOutlined, ExpandMore, OpenInNewOutlined} from '@mui/icons-material';
import {AffiliateNetworkDialog, AffiliateNetworkSection} from './AffiliateNetworkDesign';
import {SettingsDialogDescription} from '../settings/SettingsDialog';
import {getSettingsColors} from '../settings/SettingsDesign';
import {CopyTextToClipboard} from '../copy/CopyTextToClipboard';
import {buildPostbackUrl, createPostbackBuilderState, isValidPostbackEndpoint, postbackParameterGroups, postbackParameters} from './postbackUrlBuilderUtils';

export default function PostbackUrlBuilder({initialUrl, onClose}) {
    const titleId = useId();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [draft, setDraft] = useState(() => createPostbackBuilderState(initialUrl));
    const [touched, setTouched] = useState({});
    const [copied, setCopied] = useState(false);
    const [copyError, setCopyError] = useState('');
    const endpointValid = isValidPostbackEndpoint(draft.endpoint);
    const incomplete = postbackParameters.filter(parameter => draft.enabled[parameter.name] && !draft.values[parameter.name].trim());
    const url = buildPostbackUrl(draft.endpoint, draft.values, draft.enabled);
    const complete = endpointValid && incomplete.length === 0;
    const update = change => {
        setDraft(previous => ({...previous, ...change(previous)}));
        setCopied(false);
        setCopyError('');
    };
    const copy = async () => {
        try {
            if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
            await CopyTextToClipboard(url);
            setCopied(true);
        } catch {
            setCopyError('Unable to copy automatically. Select the generated URL and copy it manually.');
        }
    };

    return <AffiliateNetworkDialog open onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen} aria-labelledby={titleId}>
        <DialogTitle id={titleId}>Postback URL builder
            <SettingsDialogDescription>Choose parameters and enter your network’s tokens or values.</SettingsDialogDescription>
        </DialogTitle>
        <DialogContent sx={{display: 'grid', gap: 2}}>
            <AffiliateNetworkSection title="Postback endpoint">
                <TextField label="Endpoint URL" value={draft.endpoint} fullWidth size="small" autoFocus
                    error={!!touched.endpoint && !endpointValid}
                    helperText={touched.endpoint && !endpointValid ? 'Enter an HTTP or HTTPS URL without query parameters or a fragment.' : 'Use your tracking domain’s postback endpoint.'}
                    onBlur={() => setTouched(previous => ({...previous, endpoint: true}))}
                    onChange={event => update(() => ({endpoint: event.target.value}))}
                    slotProps={{inputLabel: {shrink: true}}}/>
            </AffiliateNetworkSection>
            {postbackParameterGroups.map((group, index) => <Accordion key={group.title} defaultExpanded={index === 0} disableGutters elevation={0}
                sx={{borderRadius: '12px !important', '&::before': {display: 'none'}, bgcolor: theme => getSettingsColors(theme).inset,
                    '& .MuiAccordionSummary-root': {px: {xs: 2, sm: 2.5}, minHeight: 68}, '& .MuiAccordionSummary-content': {my: 1.5},
                    '& .MuiAccordionDetails-root': {px: {xs: 2, sm: 2.5}, pb: 2.5, pt: 0}}}>
                <AccordionSummary expandIcon={<ExpandMore/>}>
                    <Box><Typography sx={{fontSize: 14, fontWeight: 600}}>{group.title}</Typography>
                        <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.5, lineHeight: 1.6}}>{group.description}</Typography></Box>
                </AccordionSummary>
                <AccordionDetails><Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 2}}>
                    {group.parameters.map(parameter => <Box key={parameter.name} sx={{minWidth: 0}}>
                        <FormControlLabel sx={{m: 0, mb: 0.5, '& .MuiFormControlLabel-label': {fontSize: 13}, '& .MuiCheckbox-root': {p: 0.5, ml: -0.5, mr: 0.5}}}
                            label={parameter.label + (parameter.required ? ' *' : '')}
                            control={<Checkbox size="small" checked={draft.enabled[parameter.name]} disabled={parameter.required}
                                onChange={event => {const checked = event.target.checked; update(previous => ({enabled: {...previous.enabled, [parameter.name]: checked}}));}}
                                slotProps={{input: {'aria-label': `Include ${parameter.name}`}}}/>}/>
                        <TextField name={`postback-builder-${parameter.name}`} label={parameter.name} value={draft.values[parameter.name]} fullWidth size="small"
                            disabled={!draft.enabled[parameter.name]} placeholder={parameter.placeholder}
                            error={!!touched[parameter.name] && draft.enabled[parameter.name] && !draft.values[parameter.name].trim()}
                            helperText={touched[parameter.name] && draft.enabled[parameter.name] && !draft.values[parameter.name].trim() ? 'Enter a token or value.' : parameter.description}
                            onBlur={() => setTouched(previous => ({...previous, [parameter.name]: true}))}
                            onChange={event => {const value = event.target.value; update(previous => ({values: {...previous.values, [parameter.name]: value}}));}}
                            slotProps={{inputLabel: {shrink: true}}}/>
                    </Box>)}
                </Box></AccordionDetails>
            </Accordion>)}
        </DialogContent>
        <Box sx={{px: {xs: 2, sm: 3}, pt: 1.5}}>
            <TextField label="Generated postback URL" value={url} fullWidth multiline minRows={2} maxRows={4} size="small"
                slotProps={{inputLabel: {shrink: true}, input: {readOnly: true}}}
                sx={{'& .MuiOutlinedInput-root': {fontFamily: 'monospace'}, '& textarea': {overflowWrap: 'anywhere'}}}
                onClick={event => {if (event.target.tagName === 'TEXTAREA') event.target.select();}}/>
            <Typography role="status" sx={{fontSize: 12, color: 'text.secondary', mt: 1, whiteSpace: 'normal'}}>
                {!endpointValid ? 'Enter a valid postback endpoint.' : incomplete.length ? 'Enter a value for each included parameter to complete your URL.' : 'Your URL is ready to copy.'}
            </Typography>
            {copyError && <Alert severity="error" sx={{mt: 1}}>{copyError}</Alert>}
        </Box>
        <DialogActions sx={{flexWrap: 'wrap', rowGap: 1}}>
            <Button component="a" href="https://skro.eu/docs/postback/parameters-in-postback-urls" target="_blank" rel="noopener noreferrer"
                color="inherit" size="small" endIcon={<OpenInNewOutlined fontSize="small"/>} sx={{mr: 'auto'}}>Parameter docs</Button>
            <Button onClick={onClose} color="inherit">Close</Button>
            <Button variant="contained" disabled={!complete} onClick={copy} startIcon={copied ? <Check/> : <ContentCopyOutlined/>}>{copied ? 'Copied' : 'Copy URL'}</Button>
        </DialogActions>
    </AffiliateNetworkDialog>;
}
